import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Flex,
  Input,
  Collapse,
  RadioGroup,
  Radio,
  Stack,
  Text,
  IconButton,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';

const ReferenceInputModal = ({
  isOpen,
  onClose,
  label,
  selectedReference,
  handleSaveReference,
  tags = [],
  ref_type,
  operand, // Operand passed from parent component
  tagType, // Tag type to filter against, if present
}) => {
  const [inputValue, setInputValue] = useState(selectedReference || '');
  const [isManual, setIsManual] = useState(false);
  const [expandedDevices, setExpandedDevices] = useState({});
  const [isBooleanSelection, setIsBooleanSelection] = useState(false);

  useEffect(() => {
    if (tagType === 10) {
      setIsManual(false); // Manuel giriş kapalı
      setIsBooleanSelection(false); // TRUE/FALSE başlangıçta kapalı
      setInputValue('');
    }
  }, [tagType]);

  // Determine allowed tag types for Reference 1 based on operand and tagType
  let allowedReferenceTypes = [];

  if (operand === '!=' || operand === '==') {
    // Strictly allow only type 10 tags when operand is != or <>
    allowedReferenceTypes = [10];
  } else if (tagType) {
    // If tagType is provided and operand is not != or <>, filter based on tagType
    if (tagType === 1 || tagType === 2 || tagType === 9) {
      allowedReferenceTypes = [1, 2, 9];
    } else if (tagType === 10) {
      allowedReferenceTypes = [10];
    } else if (
      [3, 4, 5, 6, 7, 8, 11, 12, 13, 14, 15, 16, 17, 18, 19].includes(tagType)
    ) {
      allowedReferenceTypes = [
        3, 4, 5, 6, 7, 8, 11, 12, 13, 14, 15, 16, 17, 18, 19,
      ];
    }
  } else {
    // If no tagType is provided and operand is not != or <>, filter based on operand
    allowedReferenceTypes = [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 11, 12, 13, 14, 15, 16, 17, 18, 19,
    ];
  }

  // Filter tags based on allowed reference types
  const filteredTags = tags.map(deviceGroup => ({
    ...deviceGroup,
    device: deviceGroup.device.map(device => ({
      ...device,
      tag: device.tag.filter(tag => allowedReferenceTypes.includes(tag.type)),
    })),
  }));

  const handleSave = () => {
    if (inputValue) {
      handleSaveReference(inputValue, ref_type);
      onClose();
    }
  };

  const handleInputChange = e => {
    setInputValue(e.target.value);
  };

  const handleSelectChange = value => {
    setInputValue(value);
  };

  const handleDeviceToggle = deviceId => {
    setExpandedDevices(prevState => ({
      ...prevState,
      [deviceId]: !prevState[deviceId],
    }));
  };

  const toggleInputType = type => {
    if (tagType === 10) {
      setIsBooleanSelection(type === 'boolean'); // Eğer type boolean ise TRUE/FALSE açılır
      setInputValue('');
    } else {
      setIsManual(type === 'manual'); // Normal manual input açılır
      setInputValue('');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{label}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex direction="column" mb={4}>
            <Button
              onClick={() => toggleInputType('tag')}
              mb={2}
              size="xs"
              variant="outline"
            >
              Select Tag
            </Button>
            <Collapse in={!isManual}>
              <RadioGroup
                onChange={handleSelectChange}
                value={inputValue}
                mt={4}
              >
                {filteredTags.map((deviceGroup, index) => (
                  <div key={index}>
                    {deviceGroup.device.map(device => (
                      <div key={device.uuid}>
                        <Flex align="center">
                          <IconButton
                            size="xs"
                            icon={
                              expandedDevices[device.uuid] ? (
                                <ChevronDownIcon />
                              ) : (
                                <ChevronRightIcon />
                              )
                            }
                            onClick={() => handleDeviceToggle(device.uuid)}
                            variant="ghost"
                            aria-label="Expand/Collapse"
                          />
                          <Text fontWeight="bold" ml={2}>
                            {device.name}
                          </Text>
                        </Flex>
                        <Collapse in={expandedDevices[device.uuid]}>
                          <Stack pl={6}>
                            {device.tag.map(tag => (
                              <Radio key={tag.id} value={tag.id}>
                                {tag.name}
                              </Radio>
                            ))}
                          </Stack>
                        </Collapse>
                      </div>
                    ))}
                  </div>
                ))}
              </RadioGroup>
            </Collapse>
            {tagType === 10 ? (
              <>
                <Button
                  onClick={() => toggleInputType('boolean')}
                  mt={2}
                  size="xs"
                  variant="outline"
                >
                  Select TRUE/FALSE
                </Button>
                <Collapse in={isBooleanSelection}>
                  <Text mt={4} fontWeight="bold">
                    Select TRUE or FALSE:
                  </Text>
                  <RadioGroup
                    onChange={setInputValue}
                    value={inputValue}
                    mt={2}
                  >
                    <Stack direction="row">
                      <Radio value="TRUE">TRUE</Radio>
                      <Radio value="FALSE">FALSE</Radio>
                    </Stack>
                  </RadioGroup>
                </Collapse>
              </>
            ) : (
              <>
                <Button
                  onClick={() => toggleInputType('manual')}
                  mt={2}
                  size="xs"
                  variant="outline"
                >
                  Manual Input
                </Button>
                <Collapse in={isManual}>
                  <Input
                    mt={4}
                    placeholder="Enter value"
                    value={inputValue}
                    onChange={handleInputChange}
                  />
                </Collapse>
              </>
            )}
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Button
            colorScheme="blue"
            mr={3}
            onClick={handleSave}
            isDisabled={!inputValue}
          >
            Save
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ReferenceInputModal;
